import React from 'react';

/**
 * The password rules, ticked off live as someone types.
 *
 * Replaces a signup flow that accepted anything 6 characters long, posted it,
 * and let the server refuse — with a message that could not say which rule was
 * broken, and (because the loading overlay outranks the alert) no way to
 * dismiss the result. The rules were always knowable up front; nothing was
 * gained by waiting for a round trip to reveal them.
 *
 * Deliberately renders EVERY rule from the first keystroke rather than only the
 * unmet ones. A list that removes items as they pass reflows under the cursor
 * exactly while it is being read, and it never shows what is still ahead.
 *
 * Hidden until the field has focus or content, so an untouched form is not a
 * wall of red crosses before anyone has typed anything.
 */
function PasswordRequirements({ results, blockers = [], show }) {
  if (!show) return null;

  return (
    <>
    <ul
      className="mt-2 space-y-1"
      // Politely announced: a screen reader user gets the updates without the
      // list interrupting them on every keystroke.
      aria-live="polite"
    >
      {results.map((r) => (
        <li
          key={r.id}
          className="flex items-start gap-2 text-[12px] leading-[1.45] transition-colors duration-150"
          style={{ color: r.ok ? 'var(--text-muted)' : 'var(--text-secondary)' }}
        >
          <span
            aria-hidden
            className="mt-[1px] inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold transition-colors duration-150"
            style={
              r.ok
                ? { backgroundColor: 'rgb(34 197 94 / 0.14)', color: 'rgb(22 163 74)' }
                : { backgroundColor: 'var(--surface-raised)', color: 'var(--text-disabled)' }
            }
          >
            {r.ok ? '✓' : ''}
          </span>
          <span className={r.ok ? 'line-through decoration-[0.5px]' : ''}>
            {r.label}
            {/* Names the offending character rather than leaving a hunt. */}
            {r.hint && (
              <span className="ml-1" style={{ color: 'rgb(220 38 38)' }}>
                — {r.hint}
              </span>
            )}
          </span>
          {/* The tick is decorative; the state has to reach assistive tech too. */}
          <span className="sr-only">{r.ok ? '(met)' : '(not met)'}</span>
        </li>
      ))}
    </ul>

    {/* Rules that are enforced but not worth a permanent tick — "not a common
        password", "doesn't contain your name". They earn a line only when they
        fail, because a disabled button with nothing explaining it is the
        complaint this whole change exists to fix. */}
    {blockers.map((msg) => (
      <p
        key={msg}
        className="mt-1.5 text-[12px] leading-[1.45]"
        style={{ color: 'rgb(220 38 38)' }}
      >
        {msg}
      </p>
    ))}
    </>
  );
}

export default PasswordRequirements;
